/**
 * FeaturesOverview page - Fourth step of onboarding
 */

import {useNavigate} from 'react-router-dom';
import {Button} from '../../components/ui/Button';
import {Container} from '../../components/ui/Container';
import {Card} from '../../components/ui/Card';
import {ProgressBar} from '../../components/onboarding/ProgressBar';
import {useOnboardingStore} from '@voxapilot/shared/stores';
import {ONBOARDING_STEPS} from '@voxapilot/shared/types';
import {ROUTES} from '../../config/constants';

const features = [
  {
    title: 'Real-Time Transcription',
    description: 'Get accurate transcriptions as your meetings happen',
    icon: 'T',
  },
  {
    title: 'AI Summaries',
    description: 'Automatically generate meeting summaries and action items',
    icon: 'S',
  },
  {
    title: 'Search & Filter',
    description: 'Find any conversation or keyword instantly',
    icon: 'F',
  },
  {
    title: 'Integrations',
    description: 'Connect with Zoom, Teams, Slack, and more',
    icon: 'I',
  },
];

export function FeaturesOverviewPage() {
  const navigate = useNavigate();
  const {completeStep, updateOnboardingData} = useOnboardingStore();

  const handleContinue = () => {
    updateOnboardingData({
      featuresViewed: features.map((f) => f.title),
    });
    completeStep(ONBOARDING_STEPS.FEATURES_OVERVIEW);
    navigate(ROUTES.ONBOARDING_COMPLETE);
  };

  const handleSkip = () => {
    completeStep(ONBOARDING_STEPS.FEATURES_OVERVIEW);
    navigate(ROUTES.ONBOARDING_COMPLETE);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <Container className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="max-w-4xl w-full space-y-8">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar />
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-textPrimary dark:text-gray-100">
                Explore Key Features
              </h1>
              <p className="text-textSecondary dark:text-gray-400">
                Here are some features that will help you get the most out of VoxaPilot
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {features.map((feature) => (
                <Card key={feature.title} hover className="p-6">
                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                      <span className="text-lg font-semibold text-gray-600 dark:text-gray-400">
                        {feature.icon}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-textPrimary dark:text-gray-100 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-textSecondary dark:text-gray-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button variant="primary" size="lg" onClick={handleContinue} className="w-full sm:w-auto">
                Continue
              </Button>
              <Button variant="ghost" size="lg" onClick={handleSkip} className="w-full sm:w-auto">
                Skip
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

